import React from 'react';
import { AppSelect } from '../../../../components/common/select';
import api from '../../../../api';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';

const ProposalHeader = ({
    companyName,
    proposalCode,
    handleBack,
    readOnly = false,
    proposalDate,
    validUntil,
    handleDateChange,
    onServiceTemplateChange,
    serviceTemplateId,
    proposalTemplateId,
    onProposalTemplateChange,
    proposalTemplateOptions = [], // Add this prop
    scope, // ✅ TAMBAHAN: Scope field
    onScopeChange, // ✅ TAMBAHAN: Scope change handler
    isccScopeOptions = [], // ✅ ISCC SCOPE: ISCC scope options
    forceIsccMode, // ✅ ISCC SCOPE: Force ISCC mode when parent knows it's ISCC
    accreditationIds, // ✅ ACCREDITATION: Array of accreditation IDs
    onAccreditationChange, // ✅ ACCREDITATION: Accreditation change handler
    accreditationOptions = [], // ✅ TAMBAHAN: Accreditation options
    bankId, // ✅ BANK: Bank ID
    onBankChange, // ✅ BANK: Bank change handler
    bankOptions = [], // ✅ BANK: Bank options
    projectName, // ✅ PROJECT NAME: Project name from lead
    onProjectNameChange, // ✅ PROJECT NAME: Project name change handler
    signatureName, // ✅ SIGNATURE NAME: Signature name field
    onSignatureNameChange, // ✅ SIGNATURE NAME: Signature name change handler
    signaturePosition, // ✅ SIGNATURE POSITION: Signature position field
    onSignaturePositionChange, // ✅ SIGNATURE POSITION: Signature position change handler
    trainingDeliveryMethod, // ✅ TRAINING DELIVERY: Training delivery method field
    onTrainingDeliveryMethodChange, // ✅ TRAINING DELIVERY: Training delivery method change handler
    // ✅ ISCC-SPECIFIC FIELDS
    turnover,
    onTurnoverChange,
    registrationNumber, // ✅ SUSTAINABILITY: Registration number
    onRegistrationNumberChange,
    facilitiesAndAddress,
    onFacilitiesAndAddressChange,
    materialInputIds,
    onMaterialInputsChange,
    materialOutputIds,
    onMaterialOutputsChange,
    ghgCalculation,
    onGhgCalculationChange,

    // ✅ CARBON PROJECT & GHG FIELDS
    // Shared fields
    objective,
    onObjectiveChange,
    sector,
    onSectorChange,
    companyId, // ✅ Company ID to update sector in company table
    materiality,
    onMaterialityChange,
    numberOfSites,
    onNumberOfSitesChange,
    numberOfFacilities,
    onNumberOfFacilitiesChange,
    siteAddress,
    onSiteAddressChange,
    facilityAddress,
    onFacilityAddressChange,

    // Carbon Project specific
    methodology,
    onMethodologyChange,
    typeOfEmissionsRemovals,
    onTypeOfEmissionsRemovalsChange,
    ghgMonitoringPeriod,
    onGhgMonitoringPeriodChange,
    ghgEmissionReductions,
    onGhgEmissionReductionsChange,

    // GHG specific
    organizationActivities,
    onOrganizationActivitiesChange,
    reportingBoundaries,
    onReportingBoundariesChange,
    claim,
    onClaimChange,
    levelOfAssurance,
    onLevelOfAssuranceChange,
    verificationLocation,
    onVerificationLocationChange
}) => {
    // Add state for options and selected values
    const [serviceTemplateOptions, setServiceTemplateOptions] = useState([]);
    const [selectedServiceTemplate, setSelectedServiceTemplate] = useState(serviceTemplateId || '');
    const [selectedProposalTemplate, setSelectedProposalTemplate] = useState(proposalTemplateId ? String(proposalTemplateId) : '');
    const [materialInputOptions, setMaterialInputOptions] = useState([]);
    const [materialOutputOptions, setMaterialOutputOptions] = useState([]);
    const [sectorOptions, setSectorOptions] = useState([]);

    // ✅ PERFORMANCE: Local state for text inputs to prevent parent re-renders on every keystroke
    const [localTextValues, setLocalTextValues] = useState({
        projectName: projectName || '',
        scope: scope || '',
        turnover: turnover || '',
        registrationNumber: registrationNumber || '',
        facilitiesAndAddress: facilitiesAndAddress || '',
        ghgCalculation: ghgCalculation || '',
        objective: objective || '',
        materiality: materiality || '',
        numberOfSites: numberOfSites || '',
        numberOfFacilities: numberOfFacilities || '',
        siteAddress: siteAddress || '',
        facilityAddress: facilityAddress || '',
        methodology: methodology || '',
        typeOfEmissionsRemovals: typeOfEmissionsRemovals || '',
        ghgEmissionReductions: ghgEmissionReductions || '',
        organizationActivities: organizationActivities || '',
        reportingBoundaries: reportingBoundaries || '',
        claim: claim || '',
        levelOfAssurance: levelOfAssurance || '',
        verificationLocation: verificationLocation || '',
        signatureName: signatureName || '',
        signaturePosition: signaturePosition || ''
    });

    // ✅ PERFORMANCE: Sync local state when props change (controlled behavior)
    useEffect(() => {
        setLocalTextValues(prev => ({
            ...prev,
            projectName: projectName || '',
            scope: scope || '',
            turnover: turnover || '',
            registrationNumber: registrationNumber || '',
            facilitiesAndAddress: facilitiesAndAddress || '',
            ghgCalculation: ghgCalculation || '',
            objective: objective || '',
            materiality: materiality || '',
            numberOfSites: numberOfSites || '',
            numberOfFacilities: numberOfFacilities || '',
            siteAddress: siteAddress || '',
            facilityAddress: facilityAddress || '',
            methodology: methodology || '',
            typeOfEmissionsRemovals: typeOfEmissionsRemovals || '',
            ghgEmissionReductions: ghgEmissionReductions || '',
            organizationActivities: organizationActivities || '',
            reportingBoundaries: reportingBoundaries || '',
            claim: claim || '',
            levelOfAssurance: levelOfAssurance || '',
            verificationLocation: verificationLocation || '',
            signatureName: signatureName || '',
            signaturePosition: signaturePosition || ''
        }));
    }, [projectName, scope, turnover, registrationNumber, facilitiesAndAddress, ghgCalculation, objective, materiality, numberOfSites, numberOfFacilities, siteAddress, facilityAddress, methodology, typeOfEmissionsRemovals, ghgEmissionReductions, organizationActivities, reportingBoundaries, claim, levelOfAssurance, verificationLocation, signatureName, signaturePosition]);

    // ✅ PERFORMANCE: Handle text input changes - only update local state
    const handleTextInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setLocalTextValues(prev => ({ ...prev, [name]: value }));
    }, []);

    // ✅ PERFORMANCE: Sync to parent on blur (not on every keystroke)
    const handleTextInputBlur = useCallback((e) => {
        const { name, value } = e.target;
        const handlers = {
            projectName: onProjectNameChange,
            scope: onScopeChange,
            turnover: onTurnoverChange,
            registrationNumber: onRegistrationNumberChange,
            facilitiesAndAddress: onFacilitiesAndAddressChange,
            ghgCalculation: onGhgCalculationChange,
            objective: onObjectiveChange,
            materiality: onMaterialityChange,
            numberOfSites: onNumberOfSitesChange,
            numberOfFacilities: onNumberOfFacilitiesChange,
            siteAddress: onSiteAddressChange,
            facilityAddress: onFacilityAddressChange,
            methodology: onMethodologyChange,
            typeOfEmissionsRemovals: onTypeOfEmissionsRemovalsChange,
            ghgEmissionReductions: onGhgEmissionReductionsChange,
            organizationActivities: onOrganizationActivitiesChange,
            reportingBoundaries: onReportingBoundariesChange,
            claim: onClaimChange,
            levelOfAssurance: onLevelOfAssuranceChange,
            verificationLocation: onVerificationLocationChange,
            signatureName: onSignatureNameChange,
            signaturePosition: onSignaturePositionChange
        };
        const handler = handlers[name];
        if (handler) {
            handler(value);
        }
    }, [onProjectNameChange, onScopeChange, onTurnoverChange, onRegistrationNumberChange, onFacilitiesAndAddressChange, onGhgCalculationChange, onObjectiveChange, onMaterialityChange, onNumberOfSitesChange, onNumberOfFacilitiesChange, onSiteAddressChange, onFacilityAddressChange, onMethodologyChange, onTypeOfEmissionsRemovalsChange, onGhgEmissionReductionsChange, onOrganizationActivitiesChange, onReportingBoundariesChange, onClaimChange, onLevelOfAssuranceChange, onVerificationLocationChange, onSignatureNameChange, onSignaturePositionChange]);

    // ✅ TRAINING DELIVERY: State for training deliver radio and delivery method
    // Initialize based on whether trainingDeliveryMethod has a value
    const hasInitialValue = trainingDeliveryMethod && trainingDeliveryMethod.trim() !== '';
    const [trainingDeliver, setTrainingDeliver] = useState(hasInitialValue ? 'yes' : 'no');

    // ✅ GHG MONITORING PERIOD: Parse existing value to extract date range
    const parseDateRange = useCallback((value) => {
        if (!value || value.trim() === '') {
            return { fromDate: '', toDate: '' };
        }
        
        // Try to parse format "dd mm yyyy to dd mm yyyy"
        // Also handle variations like "dd/mm/yyyy to dd/mm/yyyy" or "dd-mm-yyyy to dd-mm-yyyy"
        const patterns = [
            /(\d{1,2})\s+(\d{1,2})\s+(\d{4})\s+to\s+(\d{1,2})\s+(\d{1,2})\s+(\d{4})/i, // "dd mm yyyy to dd mm yyyy"
            /(\d{1,2})\/(\d{1,2})\/(\d{4})\s+to\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/i, // "dd/mm/yyyy to dd/mm/yyyy"
            /(\d{1,2})-(\d{1,2})-(\d{4})\s+to\s+(\d{1,2})-(\d{1,2})-(\d{4})/i, // "dd-mm-yyyy to dd-mm-yyyy"
        ];
        
        for (const pattern of patterns) {
            const match = value.match(pattern);
            if (match) {
                const fromDay = parseInt(match[1], 10);
                const fromMonth = parseInt(match[2], 10);
                const fromYear = parseInt(match[3], 10);
                const toDay = parseInt(match[4], 10);
                const toMonth = parseInt(match[5], 10);
                const toYear = parseInt(match[6], 10);
                
                // Validate dates
                if (fromDay >= 1 && fromDay <= 31 && fromMonth >= 1 && fromMonth <= 12 && fromYear > 0 &&
                    toDay >= 1 && toDay <= 31 && toMonth >= 1 && toMonth <= 12 && toYear > 0) {
                    // Convert to YYYY-MM-DD format for date input
                    const fromDateStr = `${fromYear}-${String(fromMonth).padStart(2, '0')}-${String(fromDay).padStart(2, '0')}`;
                    const toDateStr = `${toYear}-${String(toMonth).padStart(2, '0')}-${String(toDay).padStart(2, '0')}`;
                    
                    // Validate the date strings
                    const fromDate = new Date(fromDateStr);
                    const toDate = new Date(toDateStr);
                    if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
                        return { fromDate: fromDateStr, toDate: toDateStr };
                    }
                }
            }
        }
        
        return { fromDate: '', toDate: '' };
    }, []);

    // ✅ GHG MONITORING PERIOD: Format date as "dd/mm/yyyy" (with slashes)
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    // ✅ GHG MONITORING PERIOD: Initialize date range state
    const [dateRange, setDateRange] = useState(() => parseDateRange(ghgMonitoringPeriod));

    // ✅ GHG MONITORING PERIOD: Update date range when prop changes
    useEffect(() => {
        const parsed = parseDateRange(ghgMonitoringPeriod);
        setDateRange(parsed);
    }, [ghgMonitoringPeriod]); // parseDateRange is stable (useCallback with empty deps)

    // ✅ GHG MONITORING PERIOD: Handle date range change
    const handleDateRangeChange = (type, value) => {
        const newRange = {
            ...dateRange,
            [type]: value
        };
        setDateRange(newRange);
        
        // Format and combine dates
        if (newRange.fromDate && newRange.toDate) {
            const formattedFrom = formatDate(newRange.fromDate);
            const formattedTo = formatDate(newRange.toDate);
            const combinedValue = `${formattedFrom} to ${formattedTo}`;
            onGhgMonitoringPeriodChange?.(combinedValue);
        } else if (newRange.fromDate || newRange.toDate) {
            // If only one date is set, don't update the value yet
            // Or update with partial value if needed
            const formattedFrom = newRange.fromDate ? formatDate(newRange.fromDate) : '';
            const formattedTo = newRange.toDate ? formatDate(newRange.toDate) : '';
            if (formattedFrom && formattedTo) {
                onGhgMonitoringPeriodChange?.(`${formattedFrom} to ${formattedTo}`);
            } else {
                onGhgMonitoringPeriodChange?.('');
            }
        } else {
            onGhgMonitoringPeriodChange?.('');
        }
    };
    const trainingDeliveryOptions = [
        { id: 'Online', name: 'Online' },
        { id: 'Onsite', name: 'Onsite' },
        { id: 'Hybrid', name: 'Hybrid' },
        { id: 'Self Learning', name: 'Self Learning' }
    ];

    // Only show training delivery method for Training/Certification services
    const isTrainingOrCertification = useMemo(() => {
        const selected = serviceTemplateOptions.find(opt => String(opt.id) === String(selectedServiceTemplate));
        const name = (selected?.name || '').toLowerCase();
        const serviceId = String(selectedServiceTemplate || '');
        return (
            name.includes('training') ||
            name.includes('certification') ||
            ['1', '2'].includes(serviceId) // fallback by known IDs
        );
    }, [selectedServiceTemplate, serviceTemplateOptions]);

    // Show ISCC-only inputs when selected proposal template name includes "iscc" or "sustainability"
    const isIsccTemplate = useMemo(() => {
        // First check if parent forced ISCC mode
        if (forceIsccMode) return true;

        // Then check template options
        const selected = proposalTemplateOptions.find(opt => String(opt.id) === String(selectedProposalTemplate));
        const name = (selected?.name || '').toLowerCase();
        return name.includes('iscc') || name.includes('sustainability');
    }, [selectedProposalTemplate, proposalTemplateOptions, forceIsccMode]);

    // Show Sustainability inputs when selected service or proposal template name includes "sustainability"
    const isSustainabilityTemplate = useMemo(() => {
        const selectedService = serviceTemplateOptions.find(opt => String(opt.id) === String(selectedServiceTemplate));
        const serviceName = (selectedService?.name || '').toLowerCase();
        const selectedProposal = proposalTemplateOptions.find(opt => String(opt.id) === String(selectedProposalTemplate));
        const proposalName = (selectedProposal?.name || '').toLowerCase();
        return serviceName.includes('sustainability') || proposalName.includes('sustainability');
    }, [selectedServiceTemplate, serviceTemplateOptions, selectedProposalTemplate, proposalTemplateOptions]);

    // Show Carbon Project inputs when selected proposal template name includes "carbon"
    const isCarbonProjectTemplate = useMemo(() => {
        const selected = proposalTemplateOptions.find(opt => String(opt.id) === String(selectedProposalTemplate));
        const name = (selected?.name || '').toLowerCase();
        return name.includes('carbon');
    }, [selectedProposalTemplate, proposalTemplateOptions]);

    // Show GHG inputs when selected proposal template name includes "ghg"
    const isGhgTemplate = useMemo(() => {
        const selected = proposalTemplateOptions.find(opt => String(opt.id) === String(selectedProposalTemplate));
        const name = (selected?.name || '').toLowerCase();
        return name.includes('ghg');
    }, [selectedProposalTemplate, proposalTemplateOptions]);

    // Fetch ISCC materials and split into input/output categories
    useEffect(() => {
        if (!isIsccTemplate) return;

        const fetchIsccMaterials = async () => {
            try {
                // Use the paginated endpoint with large page size (this is the correct endpoint)
                let res;
                try {
                    res = await api.get('/md/iscc-material', {
                        params: {
                            page: 0,
                            size: 1000,
                            search: ''
                        }
                    });
                } catch (endpointError) {
                    // If paginated endpoint fails, try alternative endpoints (without /api/ prefix)
                    const alternativeEndpoints = [
                        '/md/iscc-material/all',
                        '/crm/iscc-material?page=0&size=1000&search=',
                        '/master/iscc-material?page=0&size=1000&search='
                    ];

                    for (const endpoint of alternativeEndpoints) {
                        try {
                            if (endpoint.includes('?')) {
                                res = await api.get(endpoint);
                            } else {
                                res = await api.get(endpoint);
                            }
                            break;
                        } catch (altError) {
                            // Continue to next endpoint
                            continue;
                        }
                    }

                    if (!res) {
                        throw new Error('All endpoints failed');
                    }
                }

                // Handle different response structures
                let materials = [];
                if (res && res.data) {
                    if (Array.isArray(res.data)) {
                        materials = res.data;
                    } else if (res.data.content && Array.isArray(res.data.content)) {
                        materials = res.data.content;
                    } else if (res.data.data && Array.isArray(res.data.data)) {
                        materials = res.data.data;
                    } else if (res.data.items && Array.isArray(res.data.items)) {
                        materials = res.data.items;
                    } else if (res.data.records && Array.isArray(res.data.records)) {
                        materials = res.data.records;
                    } else {
                        materials = [];
                    }
                }

                const toOption = (item) => ({ id: item.id, name: item.name });

                // Remove category filtering - show all materials for both input and output
                setMaterialInputOptions(materials.map(toOption));
                setMaterialOutputOptions(materials.map(toOption));
            } catch (error) {
                console.error('Error fetching ISCC materials:', error);
                setMaterialInputOptions([]);
                setMaterialOutputOptions([]);
            }
        };

        fetchIsccMaterials();
    }, [isIsccTemplate]);

    useEffect(() => {
        // Fetch service template options from API
        const fetchServiceTemplates = async () => {
            try {
                const res = await api.get('/crm/services/options');
                setServiceTemplateOptions(res.data.map(opt => ({ id: opt.id, name: opt.label || opt.name })));
            } catch (err) {
                setServiceTemplateOptions([]);
            }
        };
        fetchServiceTemplates();
    }, []);

    useEffect(() => {
        // Fetch sector options from API
        const fetchSectorOptions = async () => {
            try {
                const res = await api.get('/dashboard/sectors');
                if (res.data && res.data.sectors) {
                    const options = res.data.sectors.map(sector => ({ id: sector.id, name: sector.name }));
                    setSectorOptions(options);

                    // If we have a sector name but no sector ID, find the ID
                    if (sector && !sector.match(/^\d+$/)) { // If sector is not a number (ID)
                        const sectorOption = options.find(opt => opt.name === sector);
                        if (sectorOption && onSectorChange) {
                            onSectorChange(sectorOption.id.toString());
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching sector options:', err);
                setSectorOptions([]);
            }
        };
        fetchSectorOptions();
    }, [sector]); // onSectorChange intentionally omitted — new function ref on every parent render would re-fetch sectors on each keystroke

    // Remove the useEffect that fetches proposal templates since we'll use the prop
    // useEffect(() => {
    //     // Fetch proposal template options from API
    //     const fetchProposalTemplates = async () => {
    //         try {
    //             // const res = await api.get('/crm/proposal-templates/options');
    //             const res = await api.get('/crm/proposal-templates');
    //             setProposalTemplateOptions(res.data.map(opt => ({ id: opt.id, name: opt.templateName })));
    //         } catch (err) {
    //             setProposalTemplateOptions([]);
    //         }
    //     };
    //     fetchProposalTemplates();
    // }, []);

    useEffect(() => {
        setSelectedServiceTemplate(serviceTemplateId || '');
    }, [serviceTemplateId]);
    useEffect(() => {
        setSelectedProposalTemplate(proposalTemplateId ? String(proposalTemplateId) : '');
    }, [proposalTemplateId]);

    // Reset proposal template selection when options change (service template changed)
    useEffect(() => {
        if (proposalTemplateOptions.length === 0) {
            setSelectedProposalTemplate('');
        }
    }, [proposalTemplateOptions]);

    // ✅ TRAINING DELIVERY: Update training deliver radio when trainingDeliveryMethod changes
    useEffect(() => {
        // Check if trainingDeliveryMethod has a value (not null, undefined, or empty string)
        const hasValue = trainingDeliveryMethod && trainingDeliveryMethod.trim() !== '';
        setTrainingDeliver(hasValue ? 'yes' : 'no');
    }, [trainingDeliveryMethod]);

    // Clear training delivery fields once when switching to non Training/Certification templates
    useEffect(() => {
        if (isTrainingOrCertification) return;

        if (trainingDeliver !== 'no') {
            setTrainingDeliver('no');
        }
        if (trainingDeliveryMethod) {
            onTrainingDeliveryMethodChange?.('');
        }
        // Run only when the allowed template flag flips
    }, [isTrainingOrCertification]);

    return (
        <>
            <div className="page-header d-print-none">
                <div className="row align-items-center">
                    <div className="col">
                        <div className="d-flex align-items-center">
                            <a className="btn btn-outline-white d-none d-sm-inline-block me-3" onClick={handleBack}>
                                <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                                Back
                            </a>
                            <h3 className="page-title mb-0">Edit Proposal</h3>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row align-items-center" style={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
                <div className="col-md-8 mt-3">
                    <h3 className="mb-2">{companyName}</h3>
                    <p className="text-muted">{proposalCode}</p>
                </div>
                <div className="col-md-4">
                    <div className="d-flex align-items-end justify-content-end"></div>
                </div>
                <div className="col-md-12 mb-3">
                    <div className="card mt-3">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="form-group row mb-3">
                                        <label className="col-4 col-form-label">Proposal Date <span className="text-danger">*</span></label>
                                        <div className="col-8">
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="proposalDate"
                                                value={proposalDate}
                                                onChange={handleDateChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Valid Until <span className="text-danger">*</span></label>
                                        <div className="col-8">
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="validUntil"
                                                value={validUntil}
                                                onChange={handleDateChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Services Template<span className="text-danger">*</span></label>
                                        <div className="col-8">
                                            <AppSelect
                                                name="serviceTemplate"
                                                value={selectedServiceTemplate}
                                                onChange={(name, value) => {
                                                    setSelectedServiceTemplate(value);
                                                    if (typeof onServiceTemplateChange === 'function') {
                                                        onServiceTemplateChange(value);
                                                    }
                                                }}
                                                options={serviceTemplateOptions}
                                                placeholder="Select Service Template"
                                                dropdownParent="body"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Proposal Template<span className="text-danger">*</span></label>
                                        <div className="col-8">
                                            <AppSelect
                                                name="proposalTemplate"
                                                value={selectedProposalTemplate}
                                                onChange={(name, value) => {
                                                    setSelectedProposalTemplate(value);
                                                    if (typeof onProposalTemplateChange === 'function') {
                                                        onProposalTemplateChange(value);
                                                    }
                                                }}
                                                options={proposalTemplateOptions}
                                                placeholder={proposalTemplateOptions.length > 0 ? "Select Proposal Template" : "No templates available for selected service"}
                                                dropdownParent="body"
                                            />
                                        </div>
                                    </div>
                                </div>
                            
                                <div className="col-md-12">
                                    <div className="form-group row">
                                        <label className="col-2 col-form-label">Project Name</label>
                                        <div className="col-10">
                                            <textarea
                                                className="form-control"
                                                name="projectName"
                                                value={localTextValues.projectName}
                                                onChange={handleTextInputChange}
                                                onBlur={handleTextInputBlur}
                                                placeholder="Enter project name"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* ✅ SUSTAINABILITY: Registration number */}
                            {isSustainabilityTemplate && (
                                <div className="row mb-3">
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Registration Number</label>
                                            <div className="col-8">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="registrationNumber"
                                                    value={localTextValues.registrationNumber}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter registration number"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* ✅ ISCC: Additional inputs when proposal template contains "ISCC" */}
                            {isIsccTemplate && (
                                <div className="row mb-3">
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Turnover</label>
                                            <div className="col-8">
                                                <textarea
                                                    type="text"
                                                    className="form-control"
                                                    name="turnover"
                                                    value={localTextValues.turnover}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter turnover"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">No. of facilities and address (if applicable)</label>
                                            <div className="col-8">
                                                <textarea
                                                    type="text"
                                                    className="form-control"
                                                    name="facilitiesAndAddress"
                                                    value={localTextValues.facilitiesAndAddress}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter number and address"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="col-6 col-form-label">Type of material supplied</label>
                                    </div>
                                    <div className="col-md-6">

                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Input material</label>
                                            <div className="col-8">
                                                <AppSelect
                                                    name="materialInputIds"
                                                    value={materialInputIds || []}
                                                    onChange={(name, values) => {
                                                        onMaterialInputsChange?.(values || []);
                                                    }}
                                                    options={materialInputOptions}
                                                    placeholder={materialInputOptions.length > 0 ? "Select input materials" : "No materials available"}
                                                    dropdownParent="body"
                                                    valueField="id"
                                                    labelField="name"
                                                    multiple={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Output material</label>
                                            <div className="col-8">
                                                <AppSelect
                                                    name="materialOutputIds"
                                                    value={materialOutputIds || []}
                                                    onChange={(name, values) => {
                                                        onMaterialOutputsChange?.(values || []);
                                                    }}
                                                    options={materialOutputOptions}
                                                    placeholder={materialOutputOptions.length > 0 ? "Select output materials" : "No materials available"}
                                                    dropdownParent="body"
                                                    valueField="id"
                                                    labelField="name"
                                                    multiple={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">GHG calculation</label>
                                            <div className="col-8">
                                                <textarea
                                                    type="text"
                                                    className="form-control"
                                                    name="ghgCalculation"
                                                    value={localTextValues.ghgCalculation}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter GHG calculation"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ✅ CARBON PROJECT & GHG: Additional inputs for Carbon Project or GHG templates */}
                            {(isCarbonProjectTemplate || isGhgTemplate) && (
                                <div className="row mb-3">
                                    {/* Shared fields for both Carbon Project and GHG */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Objective</label>
                                            <div className="col-8">
                                                <textarea
                                                    className="form-control"
                                                    name="objective"
                                                    value={localTextValues.objective}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter objective"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Sector</label>
                                            <div className="col-8">
                                                <AppSelect
                                                    name="sector"
                                                    value={sector || ''}
                                                    onChange={async (name, value) => {
                                                        // Update local state first
                                                        if (onSectorChange) {
                                                            onSectorChange(value);
                                                        }

                                                        // Update company sector in backend if companyId is available
                                                        if (companyId && value) {
                                                            try {
                                                                await api.put(`/md/companies/${companyId}`, {
                                                                    sector: value
                                                                });
                                                                console.log('✅ Company sector updated successfully');
                                                            } catch (error) {
                                                                console.error('❌ Error updating company sector:', error);
                                                                // Optionally show error to user
                                                            }
                                                        }
                                                    }}
                                                    options={sectorOptions}
                                                    placeholder="Select sector"
                                                    dropdownParent="body"
                                                    valueField="id"
                                                    labelField="name"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Materiality - shared */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Materiality</label>
                                            <div className="col-8">
                                                <textarea
                                                    className="form-control"
                                                    name="materiality"
                                                    value={localTextValues.materiality}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter materiality"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Number of sites and facilities - shared */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">No. of Sites</label>
                                            <div className="col-8">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="numberOfSites"
                                                    value={localTextValues.numberOfSites}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter number of sites"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">No. of Facilities</label>
                                            <div className="col-8">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="numberOfFacilities"
                                                    value={localTextValues.numberOfFacilities}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter number of facilities"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Site and Facility addresses - shared */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Site Address</label>
                                            <div className="col-8">
                                                <textarea
                                                    className="form-control"
                                                    name="siteAddress"
                                                    value={localTextValues.siteAddress}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter site address"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Facility Address</label>
                                            <div className="col-8">
                                                <textarea
                                                    className="form-control"
                                                    name="facilityAddress"
                                                    value={localTextValues.facilityAddress}
                                                    onChange={handleTextInputChange}
                                                    onBlur={handleTextInputBlur}
                                                    placeholder="Enter facility address"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Carbon Project specific fields */}
                                    {isCarbonProjectTemplate && (
                                        <>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Methodology</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="methodology"
                                                            value={localTextValues.methodology}
                                                            onChange={handleTextInputChange}
                                                            onBlur={handleTextInputBlur}
                                                            placeholder="Enter methodology"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Type of Emissions/Removals</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="typeOfEmissionsRemovals"
                                                            value={localTextValues.typeOfEmissionsRemovals}
                                                            onChange={handleTextInputChange}
                                                            onBlur={handleTextInputBlur}
                                                            placeholder="Enter type of emissions/removals"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">GHG Monitoring Period</label>
                                                    <div className="col-8">
                                                        <div className="row g-2">
                                                            <div className="col-6">
                                                                <label className="small text-muted">From</label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    name="ghgMonitoringPeriodFrom"
                                                                    value={dateRange.fromDate || ''}
                                                                    onChange={(e) => handleDateRangeChange('fromDate', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-6">
                                                                <label className="small text-muted">To</label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    name="ghgMonitoringPeriodTo"
                                                                    value={dateRange.toDate || ''}
                                                                    onChange={(e) => handleDateRangeChange('toDate', e.target.value)}
                                                                    min={dateRange.fromDate || ''}
                                                                />
                                                            </div>
                                                        </div>
                                                        {ghgMonitoringPeriod && (
                                                            <small className="text-muted mt-1 d-block">
                                                                Saved: {ghgMonitoringPeriod}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">GHG Emission Reductions</label>
                                                    <div className="col-8">
                                                        <textarea
                                                            className="form-control"
                                                            name="ghgEmissionReductions"
                                                            value={ghgEmissionReductions || ''}
                                                            onChange={(e) => onGhgEmissionReductionsChange?.(e.target.value)}
                                                            placeholder="Enter GHG emission reductions"
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* GHG specific fields */}
                                    {isGhgTemplate && (
                                        <>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Organization Activities</label>
                                                    <div className="col-8">
                                                        <textarea
                                                            className="form-control"
                                                            name="organizationActivities"
                                                            value={localTextValues.organizationActivities}
                                                            onChange={handleTextInputChange}
                                                            onBlur={handleTextInputBlur}
                                                            placeholder="Enter organization activities"
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Reporting Boundaries</label>
                                                    <div className="col-8">
                                                        <textarea
                                                            className="form-control"
                                                            name="reportingBoundaries"
                                                            value={reportingBoundaries || ''}
                                                            onChange={(e) => onReportingBoundariesChange?.(e.target.value)}
                                                            placeholder="Enter reporting boundaries"
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Claim</label>
                                                    <div className="col-8">
                                                        <textarea
                                                            className="form-control"
                                                            name="claim"
                                                            value={localTextValues.claim}
                                                            onChange={handleTextInputChange}
                                                            onBlur={handleTextInputBlur}
                                                            placeholder="Enter claim"
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Level of Assurance</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="levelOfAssurance"
                                                            value={levelOfAssurance || ''}
                                                            onChange={(e) => onLevelOfAssuranceChange?.(e.target.value)}
                                                            placeholder="Enter level of assurance"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group row">
                                                    <label className="col-4 col-form-label">Verification Location</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="verificationLocation"
                                                            value={localTextValues.verificationLocation}
                                                            onChange={handleTextInputChange}
                                                            onBlur={handleTextInputBlur}
                                                            placeholder="Enter verification location"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ✅ TAMBAHAN: Accreditation Input Field */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Accreditation</label>
                                        <div className="col-8">
                                            <AppSelect
                                                name="accreditation"
                                                value={accreditationIds || []}
                                                onChange={(name, values) => {
                                                    if (onAccreditationChange) {
                                                        onAccreditationChange(values || []);
                                                    }
                                                }}
                                                options={accreditationOptions}
                                                placeholder={accreditationOptions.length > 0 ? "Select Accreditations" : "No accreditations available"}
                                                dropdownParent="body"
                                                valueField="id"
                                                labelField="name"
                                                multiple={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Bank</label>
                                        <div className="col-8">
                                            <AppSelect
                                                name="bank"
                                                value={bankId ? String(bankId) : ''}
                                                onChange={(name, value) => {
                                                    if (onBankChange) {
                                                        onBankChange(value);
                                                    }
                                                }}
                                                options={bankOptions}
                                                placeholder={bankOptions.length > 0 ? "Select Bank" : "No banks available"}
                                                dropdownParent="body"
                                                valueField="id"
                                                labelField="bankName"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* ✅ SIGNATURE NAME & POSITION: Signature fields */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Signature Name</label>
                                        <div className="col-8">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="signatureName"
                                                value={signatureName || ''}
                                                onChange={(e) => {
                                                    if (onSignatureNameChange) {
                                                        onSignatureNameChange(e.target.value);
                                                    }
                                                }}
                                                placeholder="Enter signature name (auto-filled from assigned user if empty)"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group row">
                                        <label className="col-4 col-form-label">Signature Position</label>
                                        <div className="col-8">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="signaturePosition"
                                                value={localTextValues.signaturePosition}
                                                onChange={handleTextInputChange}
                                                onBlur={handleTextInputBlur}
                                                placeholder="Enter signature position (auto-filled from assigned user if empty)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* ✅ TRAINING DELIVERY: Only show for Training/Certification templates */}
                            {isTrainingOrCertification && (
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group row">
                                            <label className="col-4 col-form-label">Training Deliver</label>
                                            <div className="col-8">
                                                <div className="d-flex gap-3">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="trainingDeliver"
                                                            id="trainingDeliverYes"
                                                            value="yes"
                                                            checked={trainingDeliver === 'yes'}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setTrainingDeliver(value);
                                                                // If changing to "no", clear the delivery method
                                                                if (value === 'no' && onTrainingDeliveryMethodChange) {
                                                                    onTrainingDeliveryMethodChange('');
                                                                }
                                                            }}
                                                        />
                                                        <label className="form-check-label" htmlFor="trainingDeliverYes">
                                                            Yes
                                                        </label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="trainingDeliver"
                                                            id="trainingDeliverNo"
                                                            value="no"
                                                            checked={trainingDeliver === 'no'}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setTrainingDeliver(value);
                                                                // If changing to "no", clear the delivery method
                                                                if (value === 'no' && onTrainingDeliveryMethodChange) {
                                                                    onTrainingDeliveryMethodChange('');
                                                                }
                                                            }}
                                                        />
                                                        <label className="form-check-label" htmlFor="trainingDeliverNo">
                                                            No
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {trainingDeliver === 'yes' && (
                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-4 col-form-label">Delivery Method</label>
                                                <div className="col-8">
                                                    <AppSelect
                                                        name="trainingDeliveryMethod"
                                                        value={trainingDeliveryMethod || ''}
                                                        onChange={(name, value) => {
                                                            if (onTrainingDeliveryMethodChange) {
                                                                onTrainingDeliveryMethodChange(value);
                                                            }
                                                        }}
                                                        options={trainingDeliveryOptions}
                                                        placeholder="Select Delivery Method"
                                                        dropdownParent="body"
                                                        valueField="id"
                                                        labelField="name"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProposalHeader; 